import { loggerFor } from '../helpers/debug.js';
import { Subscribable, UNKNOWN } from '../trx/Subscribable.js';
import { withTransaction } from '../trx/Transaction.js';
import { compareCost, Fact } from './Fact.js';
import { parseExpression } from './parseExpression.js';
export const log = loggerFor('Facts');
/**
 * The Container class can be used to register and query facts.
 */
export class Container extends Subscribable {
    constructor(parent) {
        super();
        this.parent = parent;
        this.facts = new Map();
        this.deriveds = new Set();
    }
    /**
     * Register a fact with this container.
     * This overrides the previously-set value for the fact given by the name.
     * Namespace facts can be queried both with and without prefix. The namespace separator is the slash.
     * @param name the name of the fact to set
     * @param value the new fact value to set
     * @param namespace an optional namespace prefix to register the fact under.
     */
    know(name, value, namespace) {
        withTransaction(() => {
            if (namespace && name.includes('/')) {
                log.warn('Not registering', name, 'because it includes a namespace but it is itself under namespace', namespace);
            }
            else {
                this.inherit(name, value, [], this);
            }
            if (namespace) {
                this.inherit(`${namespace}/${name}`, value, [], this);
            }
        });
    }
    /**
     * Returns an observable that evaluates the given expression.
     * @param expression the expression to evaluate
     * @returns an observable that emits the calculated result whenever one of the used fact variables change
     */
    query(expression) {
        return parseExpression(expression, this.getOrDefault.bind(this));
    }
    /**
     * Returns an observable that evaluates to the value of the passed fact.
     * This can be called before the fact is known, in which case the observable will sit idle until then.
     * @param name the name of the fact to query
     * @returns an observable that emits the value of the passed fact whenever it changes
     */
    queryFact(name) {
        return this.getOrDefault(name);
    }
    /**
     * Returns a new container that inherits all facts from this container (and whose facts inherit into this, though with a lower precedence).
     */
    derive() {
        const result = new Container(this);
        this.deriveds.add(result);
        for (const [name, fact] of this.facts) {
            if (fact.cost !== undefined) {
                // Inherit known facts to new derived container
                result.inherit(name, fact.get(), [...fact.cost, 1 /* Costs.DOWN */], this);
            }
        }
        return result;
    }
    /**
     * Signals that a fact has been set on a container and should be inherited by this one (and from this to other related containers).
     */
    inherit(name, value, cost, source) {
        var _a;
        const fact = this.getOrDefault(name, value);
        if (compareCost(fact.cost, cost) < 0) {
            // Current fact has lower cost, don’t update fact, don’t inherit
            return;
        }
        fact.set(value);
        this.changed();
        fact.cost = cost;
        if (source !== this.parent) {
            (_a = this.parent) === null || _a === void 0 ? void 0 : _a.inherit(name, value, [...cost, 2 /* Costs.UP */], this);
        }
        for (const derived of this.deriveds) {
            if (source !== derived) {
                derived.inherit(name, value, [...cost, 1 /* Costs.DOWN */], this);
            }
        }
    }
    /**
     * Creates a new fact with the given value only if it doesn’t exist yet.
     * The created fact has the lowest precedence.
     * @param name the fact name
     * @param value the fact value. If omitted, the fact is assumed to not yet be known
     * @returns an existing fact if it is known or a newly-created one otherwise
     */
    getOrDefault(name, value = UNKNOWN) {
        if (this.facts.has(name)) {
            return this.facts.get(name);
        }
        const fact = new Fact(value);
        this.facts.set(name, fact);
        return fact;
    }
    /**
     * The container is a subscribable that emits the names of all known facts whenever one of them changes.
     * @returns the names of all known facts, in order of when we first gained knowledge of them
     */
    get() {
        return [...this.facts.keys()];
    }
}
